import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { LogoMark } from "../components/site/Logo";

export default function PaymentResult() {
  const { pathname, search } = useLocation();
  const cancelled = pathname.endsWith("/cancel");
  const sessionId = new URLSearchParams(search).get("session_id");
  const [status, setStatus] = useState(cancelled ? "cancelled" : "pending");

  useEffect(() => {
    if (cancelled || !sessionId) return;
    let tries = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
      } catch (e) { /* keep polling */ }
      if (++tries < 10) setTimeout(tick, 2000);
      else setStatus("timeout");
    };
    tick();
  }, [sessionId, cancelled]);

  return (
    <main data-testid="payment-result-page" className="min-h-screen grid place-items-center px-4">
      <div className="sticker rounded-[32px] bg-white p-10 max-w-md text-center">
        {cancelled ? (
          <>
            <XCircle className="mx-auto w-16 h-16 text-[var(--ink-soft)]" />
            <h1 className="font-display font-bold text-4xl mt-6">No worries!</h1>
            <p className="mt-3 text-[var(--ink-soft)]">Your booking request is still with us. Pay anytime, or wait for our call.</p>
          </>
        ) : status === "paid" ? (
          <>
            <CheckCircle2 className="mx-auto w-16 h-16 text-[var(--grass)]" />
            <h1 className="font-display font-bold text-4xl mt-6">Payment received!</h1>
            <p className="mt-3 text-[var(--ink-soft)]">Thank you! The kids are doing a happy dance. We'll be in touch about your sign.</p>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto w-16 h-16 text-[var(--sun)] animate-spin" />
            <h1 className="font-display font-bold text-4xl mt-6">Checking your payment...</h1>
          </>
        )}
        <motion.div whileTap={{ scale: 0.96 }}>
          <Link
            to="/"
            data-testid="payment-home-link"
            className="mt-8 inline-block sticker-sm rounded-full bg-[var(--sun)] px-6 py-3 font-display font-semibold"
          >
            Back to the site
          </Link>
        </motion.div>
      </div>
    </main>
  );
}
